<template>
  <div class="franchise-sync-overview">
    <div v-if="loading" class="loading-row">
      <i class="fas fa-spinner fa-spin"></i> Loading sync status...
    </div>

    <div v-else-if="error" class="error-row">
      <i class="fas fa-exclamation-triangle"></i> {{ error }}
    </div>

    <template v-else-if="dashboard">
      <div class="sync-health" :class="syncHealthClass">
        <div class="health-indicator">
          <i :class="syncHealthIcon"></i>
          <div>
            <div class="health-label">FranConnect Sync</div>
            <div class="health-value">
              {{ syncStatusLabel }}
              <span v-if="dashboard.sync?.last_sync_at" class="health-time">
                · {{ formatDateTime(dashboard.sync.last_sync_at) }}
              </span>
            </div>
          </div>
        </div>
        <div v-if="canManageSync && dashboard.config" class="sync-actions">
          <button
            class="btn-sync"
            :disabled="syncing || dashboard.sync?.last_sync_status === 'running'"
            @click="triggerSync"
          >
            <i class="fas fa-sync-alt" :class="{ 'fa-spin': syncing }"></i>
            Run Sync
          </button>
        </div>
      </div>

      <div class="kpi-grid">
        <div class="kpi-card">
          <div class="kpi-value">{{ dashboard.sync?.created ?? 0 }}</div>
          <div class="kpi-label">Created (last run)</div>
        </div>
        <div class="kpi-card">
          <div class="kpi-value">{{ dashboard.sync?.updated ?? 0 }}</div>
          <div class="kpi-label">Updated (last run)</div>
        </div>
        <div class="kpi-card">
          <div class="kpi-value">{{ dashboard.sync?.deactivated ?? 0 }}</div>
          <div class="kpi-label">Deactivated</div>
        </div>
        <div class="kpi-card" :class="{ 'kpi-alert': (dashboard.sync?.errors ?? 0) > 0 }">
          <div class="kpi-value">{{ dashboard.sync?.errors ?? 0 }}</div>
          <div class="kpi-label">Upsert Errors</div>
        </div>
        <div class="kpi-card" :class="{ 'kpi-warn': (dashboard.sync?.duplicate_count ?? 0) > 0 }">
          <div class="kpi-value">{{ dashboard.sync?.duplicate_count ?? 0 }}</div>
          <div class="kpi-label">Duplicates</div>
        </div>
        <div class="kpi-card kpi-highlight">
          <div class="kpi-value">{{ fcOpenCount }}</div>
          <div class="kpi-label">FC Open store #s</div>
        </div>
        <div class="kpi-card">
          <div class="kpi-value">{{ inDevelopmentCount }}</div>
          <div class="kpi-label">In Development store #s</div>
        </div>
        <div class="kpi-card">
          <div class="kpi-value">{{ fcRosterCount }}</div>
          <div class="kpi-label">FC roster (Open + In Dev)</div>
        </div>
        <div class="kpi-card" :class="{ 'kpi-warn': openIssues > 0 }">
          <div class="kpi-value">{{ openIssues }}</div>
          <div class="kpi-label">Open Sync Issues</div>
        </div>
      </div>

      <div v-if="compareSummary" class="compare-summary">
        <div class="compare-summary-header">
          <h3>FC vs DB Reconciliation</h3>
          <button
            v-if="gapCount > 0"
            type="button"
            class="btn-review-gaps"
            @click="emit('open-reconciliation')"
          >
            Review {{ gapCount }} missing location(s) →
          </button>
        </div>
        <div class="compare-grid">
          <div class="compare-item">
            <span class="compare-label">FC sync filter units</span>
            <span class="compare-value">{{ compareSummary.fc_sync_filter_match ?? '—' }}</span>
          </div>
          <div class="compare-item">
            <span class="compare-label">DB active subjects</span>
            <span class="compare-value">{{ compareSummary.db_active_stores ?? '—' }}</span>
          </div>
          <div class="compare-item">
            <span class="compare-label">Matched in DB</span>
            <span class="compare-value">{{ compareSummary.matched ?? '—' }}</span>
          </div>
          <div class="compare-item compare-highlight">
            <span class="compare-label">New in FC filter (not DB)</span>
            <span class="compare-value">{{ compareSummary.new_in_fc_not_db?.count ?? gapCount }}</span>
          </div>
          <div class="compare-item">
            <span class="compare-label">Dropped from FC filter</span>
            <span class="compare-value">{{ compareSummary.closed_or_dropped_from_fc?.count ?? '—' }}</span>
          </div>
        </div>
        <p class="compare-note">
          FC Open store #s (FranConnect lifecycle Open) are the roster source of truth — not compliance status.
          Reconcile gaps so ADI matches FranConnect Open + In Development stores at Permit / Construction (8/9).
        </p>
        <p v-if="compareSummary.captured_at" class="compare-captured">
          Snapshot from {{ formatDateTime(compareSummary.captured_at) }}
        </p>
      </div>

      <div v-if="!dashboard.config" class="no-config">
        <i class="fas fa-plug"></i>
        <p>No FranConnect config found for this project. Contact platform admin to enable sync.</p>
      </div>
    </template>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import api from '@/services/api'
import auth from '@/services/auth'

const props = defineProps({
  projectId: { type: Number, required: true },
})

const emit = defineEmits(['issues-count', 'gap-count', 'loaded', 'open-reconciliation'])

const loading = ref(true)
const error = ref(null)
const dashboard = ref(null)
const syncing = ref(false)

const canManageSync = computed(() => auth.hasPlatformAccess() || auth.hasCrossOrgComplianceAccess())

const syncHealthClass = computed(() => {
  const status = dashboard.value?.sync?.last_sync_status
  if (status === 'success') return 'health-success'
  if (status === 'error') return 'health-error'
  if (status === 'running') return 'health-running'
  return 'health-neutral'
})

const syncHealthIcon = computed(() => {
  const status = dashboard.value?.sync?.last_sync_status
  if (status === 'success') return 'fas fa-check-circle'
  if (status === 'error') return 'fas fa-times-circle'
  if (status === 'running') return 'fas fa-sync-alt fa-spin'
  return 'fas fa-clock'
})

const syncStatusLabel = computed(() => {
  const status = dashboard.value?.sync?.last_sync_status || 'unknown'
  const env = dashboard.value?.config?.environment
  return env ? `${env} · ${status}` : status
})

const fcOpenCount = computed(() => (
  dashboard.value?.summary?.fc_open_store_numbers_at_sync
  ?? dashboard.value?.summary?.open_store_numbers
  ?? 0
))
const inDevelopmentCount = computed(() => (
  dashboard.value?.summary?.fc_in_development_store_numbers_at_sync
  ?? dashboard.value?.summary?.fc_in_development_store_numbers
  ?? 0
))
const fcRosterCount = computed(() => (
  dashboard.value?.summary?.fc_roster_store_numbers_at_sync
  ?? dashboard.value?.summary?.fc_roster_store_numbers
  ?? 0
))
const openIssues = computed(() => dashboard.value?.issue_counts?.total_open ?? 0)
const compareSummary = computed(() => dashboard.value?.compare_summary)
const gapCount = computed(() => {
  const fromSummary = compareSummary.value?.new_in_fc_not_db?.count
  if (fromSummary != null) return fromSummary
  return dashboard.value?.issue_counts?.gap_fc_not_db ?? 0
})

async function loadDashboard() {
  loading.value = true
  error.value = null
  try {
    const response = await api.get(`/franconnect/projects/${props.projectId}/ops-dashboard/`)
    dashboard.value = response.data?.data || null
    emit('issues-count', openIssues.value)
    emit('gap-count', gapCount.value)
    emit('loaded', dashboard.value)
  } catch (err) {
    error.value = err.response?.data?.error || err.message || 'Failed to load dashboard'
  } finally {
    loading.value = false
  }
}

async function triggerSync() {
  const configId = dashboard.value?.config?.id
  if (!configId) return
  syncing.value = true
  try {
    await api.post(`/franconnect/configs/${configId}/sync/`)
    await pollSyncStatus(configId)
    await loadDashboard()
  } catch (err) {
    error.value = err.response?.data?.error || 'Sync trigger failed'
  } finally {
    syncing.value = false
  }
}

async function pollSyncStatus(configId, attempts = 30) {
  for (let i = 0; i < attempts; i += 1) {
    await new Promise((r) => setTimeout(r, 3000))
    const response = await api.get(`/franconnect/configs/${configId}/sync_status/`)
    const status = response.data?.last_sync_status
    if (status !== 'running') break
  }
}

function formatDateTime(dateString) {
  if (!dateString) return ''
  return new Date(dateString).toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

watch(() => props.projectId, loadDashboard)

onMounted(loadDashboard)

defineExpose({ refresh: loadDashboard })
</script>

<style scoped>
.sync-health {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.25rem;
  border-radius: 12px;
  margin-bottom: 1.5rem;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
}

.health-success { border-color: #86efac; background: #f0fdf4; }
.health-error { border-color: #fca5a5; background: #fef2f2; }
.health-running { border-color: #93c5fd; background: #eff6ff; }

.health-indicator {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.health-indicator i {
  font-size: 1.5rem;
}

.health-label {
  font-size: 0.8rem;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.health-value {
  font-weight: 600;
  color: #1e293b;
}

.health-time {
  font-weight: 400;
  color: #64748b;
}

.btn-sync {
  padding: 0.55rem 1rem;
  border: none;
  border-radius: 8px;
  background: #1e40af;
  color: white;
  font-weight: 600;
  cursor: pointer;
}

.btn-sync:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.kpi-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(130px, 1fr));
  gap: 1rem;
  margin-bottom: 1.5rem;
}

.kpi-card {
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 1rem;
  text-align: center;
}

.kpi-highlight {
  border-color: #1e40af;
  background: #eff6ff;
}

.kpi-alert .kpi-value { color: #dc2626; }
.kpi-warn .kpi-value { color: #d97706; }

.kpi-value {
  font-size: 1.75rem;
  font-weight: 700;
  color: #1e293b;
}

.kpi-label {
  font-size: 0.75rem;
  color: #64748b;
  margin-top: 0.25rem;
}

.compare-summary {
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 1.25rem;
}

.compare-summary-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1rem;
}

.compare-summary-header h3 {
  margin: 0;
  font-size: 1rem;
}

.btn-review-gaps {
  border: none;
  background: #1e40af;
  color: white;
  font-weight: 600;
  font-size: 0.8rem;
  padding: 0.45rem 0.85rem;
  border-radius: 8px;
  cursor: pointer;
  white-space: nowrap;
}

.compare-highlight .compare-value {
  color: #d97706;
}

.compare-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 1rem;
}

.compare-item {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.compare-label {
  font-size: 0.8rem;
  color: #64748b;
}

.compare-value {
  font-size: 1.25rem;
  font-weight: 700;
}

.compare-captured {
  margin: 1rem 0 0;
  font-size: 0.8rem;
  color: #94a3b8;
}

.compare-note {
  margin: 0.75rem 0 0;
  font-size: 0.8rem;
  color: #64748b;
  line-height: 1.4;
}

.loading-row,
.error-row,
.no-config {
  padding: 2rem;
  text-align: center;
  color: #64748b;
}
</style>
